// Shared logical coordinate space for 1:1 square Janggi board rendering, last-move paths, hints, VFX, and input hit-testing.
export type Side = 'cho' | 'han';
export interface Pos {
  row: number;
  col: number;
}
export interface Point {
  x: number;
  y: number;
}
export interface ClientBox {
  left: number;
  top: number;
  width: number;
  height: number;
}
export interface BoardGeometry {
  width: number;
  height: number;
  padX: number;
  padY: number;
  baseRadius?: number;
}

export type HitResult = { kind: 'piece' | 'intersection'; pos: Pos } | null;

export const BASE_PIECE_RADIUS = 32;
export const SQUARE_BOARD: BoardGeometry = { width: 760, height: 760, padX: 48, padY: 48, baseRadius: 32 };
export const ORIGINAL_BOARD: BoardGeometry = { width: 760, height: 860, padX: 48, padY: 52, baseRadius: 34 };

export function pieceRadius(scale: number, g: BoardGeometry = SQUARE_BOARD): number {
  return (g.baseRadius ?? BASE_PIECE_RADIUS) * scale;
}

export function octagonVertices(center: Point, radius: number): Point[] {
  const pts: Point[] = [];
  for (let i = 0; i < 8; i++) {
    const a = Math.PI / 8 + (i * Math.PI) / 4;
    pts.push({ x: center.x + radius * Math.cos(a), y: center.y + radius * Math.sin(a) });
  }
  return pts;
}

function valid(g: BoardGeometry): boolean {
  return (
    [g.width, g.height, g.padX, g.padY].every(Number.isFinite) &&
    g.padX >= 0 &&
    g.padY >= 0 &&
    g.width > 2 * g.padX &&
    g.height > 2 * g.padY
  );
}

export function cellSize(g: BoardGeometry): Point {
  if (!valid(g)) throw new RangeError('Invalid board geometry');
  return { x: (g.width - 2 * g.padX) / 8, y: (g.height - 2 * g.padY) / 9 };
}

export function boardToPoint(pos: Pos, side: Side, g: BoardGeometry): Point {
  if (
    !Number.isInteger(pos.row) ||
    !Number.isInteger(pos.col) ||
    pos.row < 0 ||
    pos.row > 9 ||
    pos.col < 0 ||
    pos.col > 8
  ) {
    throw new RangeError('Invalid board position');
  }
  const cell = cellSize(g);
  const col = side === 'cho' ? pos.col : 8 - pos.col;
  const row = side === 'cho' ? pos.row : 9 - pos.row;
  return { x: g.padX + col * cell.x, y: g.padY + row * cell.y };
}

// box is the actual canvas CONTENT box in viewport CSS pixels, not its parent wrapper.
// Canvas itself has zero border/padding; devicePixelRatio belongs only to backing-store resolution.
export function clientToPoint(clientX: number, clientY: number, box: ClientBox, g: BoardGeometry): Point | null {
  if (
    !valid(g) ||
    ![clientX, clientY, box.left, box.top, box.width, box.height].every(Number.isFinite) ||
    box.width <= 0 ||
    box.height <= 0
  ) {
    return null;
  }
  const x = ((clientX - box.left) * g.width) / box.width;
  const y = ((clientY - box.top) * g.height) / box.height;
  if (x < 0 || y < 0 || x > g.width || y > g.height) return null;
  return { x, y };
}

export function pointToBoard(point: Point, side: Side, g: BoardGeometry): Pos | null {
  const cell = cellSize(g);
  if (![point.x, point.y].every(Number.isFinite)) return null;
  const col = Math.round((point.x - g.padX) / cell.x) + 0;
  const row = Math.round((point.y - g.padY) / cell.y) + 0;
  if (col < 0 || col > 8 || row < 0 || row > 9) return null;
  return { col: side === 'cho' ? col : 8 - col, row: side === 'cho' ? row : 9 - row };
}

export interface DrawnPiece {
  pos: Pos;
  center: Point;
  radius: number;
  // Pass only selectable stable pieces; animated/pending ghosts are not logical pieces.
}

export function insideOctagon(point: Point, center: Point, radius: number): boolean {
  if (!(radius > 0) || ![point.x, point.y, center.x, center.y, radius].every(Number.isFinite)) return false;
  let sign = 0;
  for (let i = 0; i < 8; i++) {
    const a = Math.PI / 8 + (i * Math.PI) / 4;
    const b = a + Math.PI / 4;
    const ax = center.x + radius * Math.cos(a);
    const ay = center.y + radius * Math.sin(a);
    const bx = center.x + radius * Math.cos(b);
    const by = center.y + radius * Math.sin(b);
    const cross = (bx - ax) * (point.y - ay) - (by - ay) * (point.x - ax);
    if (Math.abs(cross) < 1e-7) continue;
    const next = Math.sign(cross);
    if (sign && next !== sign) return false;
    sign = next;
  }
  return true;
}

export const pointInOctagon = insideOctagon;

// Exact rendered octagon polygon first (in reverse draw order), then nearest grid intersection.
export function hitBoardDetailed(
  point: Point,
  side: Side,
  g: BoardGeometry,
  pieces: DrawnPiece[] = []
): HitResult {
  if (![point.x, point.y].every(Number.isFinite) || point.x < 0 || point.y < 0 || point.x > g.width || point.y > g.height) {
    return null;
  }
  for (let i = pieces.length - 1; i >= 0; i--) {
    if (insideOctagon(point, pieces[i].center, pieces[i].radius)) {
      return { kind: 'piece', pos: pieces[i].pos };
    }
  }
  const inter = pointToBoard(point, side, g);
  return inter ? { kind: 'intersection', pos: inter } : null;
}

export function hitBoard(point: Point, side: Side, g: BoardGeometry, pieces: DrawnPiece[] = []): Pos | null {
  const res = hitBoardDetailed(point, side, g, pieces);
  return res ? res.pos : null;
}

export interface ResolveBoardInputOptions {
  selectedPos?: Pos | null;
  legalTargets?: Pos[];
  selectableMyPieces?: Pos[];
  isDragRelease?: boolean;
}

// Mobile thumb-friendly input resolver:
// 1) Preserves exact octagon piece priority when switching own pieces on tap.
// 2) Magnetically snaps slightly off-center taps/releases to the nearest legal destination (eliminates "밀리는 감도" / accidental deselect).
// 3) Expands small pieces (e.g. 졸/사) with a non-overlapping thumb selection halo (36.5 logical px).
export function resolveBoardInput(
  point: Point,
  side: Side,
  g: BoardGeometry,
  pieces: DrawnPiece[] = [],
  opts: ResolveBoardInputOptions = {}
): HitResult {
  if (![point.x, point.y].every(Number.isFinite) || point.x < 0 || point.y < 0 || point.x > g.width || point.y > g.height) {
    return null;
  }

  const mySet = new Set((opts.selectableMyPieces || []).map(p => `${p.row},${p.col}`));
  const legalTargets = opts.legalTargets || [];
  const selectedKey = opts.selectedPos ? `${opts.selectedPos.row},${opts.selectedPos.col}` : '';

  // 1) When tapping (not releasing a drag), direct octagon hit on another own piece immediately switches piece selection
  if (!opts.isDragRelease && mySet.size > 0) {
    for (let i = pieces.length - 1; i >= 0; i--) {
      const p = pieces[i];
      const key = `${p.pos.row},${p.pos.col}`;
      if (mySet.has(key) && key !== selectedKey && insideOctagon(point, p.center, p.radius * 1.04)) {
        return { kind: 'piece', pos: p.pos };
      }
    }
  }

  // 2) Magnetic legal-target snapping when a piece is selected
  if (opts.selectedPos && legalTargets.length > 0) {
    const snapRadius = opts.isDragRelease ? 56 : 48;
    let bestTarget: Pos | null = null;
    let bestDist = Infinity;

    for (const t of legalTargets) {
      const tp = boardToPoint(t, side, g);
      const d = Math.hypot(point.x - tp.x, point.y - tp.y);
      if (d <= snapRadius && d < bestDist) {
        bestDist = d;
        bestTarget = t;
      }
    }

    if (bestTarget) {
      // Only yield to another own piece if the finger is distinctly closer to that own piece's center
      let closerOwnPiece = false;
      if (!opts.isDragRelease && mySet.size > 0) {
        for (const p of pieces) {
          const key = `${p.pos.row},${p.pos.col}`;
          if (mySet.has(key) && key !== selectedKey) {
            const dOwn = Math.hypot(point.x - p.center.x, point.y - p.center.y);
            if (dOwn + 4 < bestDist && dOwn <= 36.5) {
              closerOwnPiece = true;
              break;
            }
          }
        }
      }
      if (!closerOwnPiece) {
        return { kind: 'intersection', pos: bestTarget };
      }
    }
  }

  // 3) Exact rendered octagon hit on any piece
  for (let i = pieces.length - 1; i >= 0; i--) {
    if (insideOctagon(point, pieces[i].center, pieces[i].radius)) {
      return { kind: 'piece', pos: pieces[i].pos };
    }
  }

  // 4) Non-overlapping thumb-friendly halo (up to 36.5 logical px < cell.y/2 = 36.89) for own pieces (especially 졸/사)
  if (!opts.isDragRelease && mySet.size > 0) {
    let bestOwn: Pos | null = null;
    let bestOwnDist = Infinity;
    for (let i = pieces.length - 1; i >= 0; i--) {
      const p = pieces[i];
      const key = `${p.pos.row},${p.pos.col}`;
      if (!mySet.has(key)) continue;
      const halo = Math.min(36.6, Math.max(p.radius * 1.18, 36.2));
      const d = Math.hypot(point.x - p.center.x, point.y - p.center.y);
      if (d <= halo && d < bestOwnDist) {
        bestOwnDist = d;
        bestOwn = p.pos;
      }
    }
    if (bestOwn) {
      return { kind: 'piece', pos: bestOwn };
    }
  }

  // 5) Fallback to nearest grid intersection
  const inter = pointToBoard(point, side, g);
  return inter ? { kind: 'intersection', pos: inter } : null;
}

export function fitBoard(container: ClientBox, g: BoardGeometry): ClientBox {
  if (!valid(g) || !(container.width > 0) || !(container.height > 0)) {
    throw new RangeError('Invalid viewport');
  }
  const scale = Math.min(container.width / g.width, container.height / g.height);
  const width = g.width * scale;
  const height = g.height * scale;
  return {
    left: container.left + (container.width - width) / 2,
    top: container.top + (container.height - height) / 2,
    width,
    height
  };
}

export function backingStore(box: ClientBox, g: BoardGeometry, dpr: number) {
  if (!valid(g) || !(box.width > 0) || !(box.height > 0) || !(dpr > 0) || !Number.isFinite(dpr)) {
    throw new RangeError('Invalid backing store');
  }
  const clampedDpr = Math.min(2, dpr);
  const width = Math.max(1, Math.round(box.width * clampedDpr));
  const height = Math.max(1, Math.round(box.height * clampedDpr));
  return { width, height, scaleX: width / g.width, scaleY: height / g.height };
}
